import { JanusJS } from 'lib/janus';
import { logger } from 'utils/logger';
import { PublishIntent } from 'utils/webrtc/types';

// Accessors, not resolved values: `getActiveMediaConfig` clones the screenshare stream and registers
// the clone, so a pure-remove intent must never reach it.
export type PublishTracksHost = {
  getAudioMid: () => string | undefined;
  getVideoMid: () => string | undefined;
  getTransceiverMid: (kind: 'audio' | 'video') => string | undefined;
  getVideoTrackType: () => 'video' | 'screen';
  getActiveMediaConfig: () => {
    audio?: JanusJS.TrackOption['capture'];
    video?: JanusJS.TrackOption['capture'];
  };
  isSimulcastEnabled: () => boolean;
  getSimulcastMaxBitrates: () => NonNullable<JanusJS.TrackOption['simulcastMaxBitrates']>;
  getSendEncodings: () => JanusJS.TrackOption['sendEncodings'] | null;
};

// Resolved mid ⇒ replace, no mid ⇒ add. `capture` stays constraints (not a track) so janus can
// bind a carried offer.stream; `config` resolves lazily since a pure-remove mustn't clone tracks.
export const buildPublishTracks = (
  intent: PublishIntent,
  host: PublishTracksHost
): JanusJS.TrackOption[] => {
  const tracks: JanusJS.TrackOption[] = [];
  const needsConfig = intent.audio === 'send' || intent.video === 'send';
  const config = needsConfig ? host.getActiveMediaConfig() : {};

  if (intent.audio === 'send') {
    const mid = host.getAudioMid() ?? host.getTransceiverMid('audio');
    tracks.push({
      type: 'audio',
      recv: false,
      capture: config.audio ?? true,
      // A publish carries new mute intent, so the fresh capture must stay live; janus otherwise
      // copies the outgoing (muted) track's `enabled` onto it and the publisher is silent.
      preserveMuteState: false,
      ...(mid ? { replace: true, mid } : { add: true }),
    });
  } else if (intent.audio === 'remove') {
    const mid = host.getAudioMid() ?? host.getTransceiverMid('audio');
    if (mid) {
      tracks.push({ type: 'audio', mid, remove: true });
    }
  }

  if (intent.video === 'send') {
    const mid = host.getVideoMid();
    const simulcast = host.isSimulcastEnabled();
    const sendEncodings = host.getSendEncodings();
    tracks.push({
      type: host.getVideoTrackType(),
      recv: false,
      capture: config.video ?? true,
      ...(simulcast
        ? {
            simulcast: true,
            simulcastMaxBitrates: host.getSimulcastMaxBitrates(),
            ...(sendEncodings ? { sendEncodings } : {}),
          }
        : {}),
      ...(mid ? { replace: true, mid } : { add: true }),
    });
  } else if (intent.video === 'remove') {
    const mid = host.getVideoMid();
    if (mid) {
      tracks.push({ type: host.getVideoTrackType(), mid, remove: true });
    }
  }

  logger.remote({ system: true, capture: 'streaming' }).debug('Built publish tracks:', {
    intent,
    tracks: tracks.map((track) => {
      let op: 'remove' | 'replace' | 'add' = 'add';
      if (track.remove) {
        op = 'remove';
      } else if (track.replace) {
        op = 'replace';
      }
      return {
        type: track.type,
        mid: track.mid,
        op,
        deviceId:
          typeof track.capture === 'object' &&
          track.capture !== null &&
          'deviceId' in (track.capture as object)
            ? (track.capture as MediaTrackConstraints).deviceId
            : undefined,
      };
    }),
  });

  return tracks;
};
